/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext } from 'react'
import { FsiType } from '../../constants/fsi'
import Img from '../Img/index'
import { getAssetPath } from '../../utils/assetPath'
import { ServiceContext } from '../../root'

// TypeScript interfaces
export interface TreeNode {
  id: number | string // Can be string for virtual folders
  name: string
  type: string
  level?: number
  parentId?: number | string | null
  parent?: TreeNode // Reference to parent node
  hasChildren?: boolean
  wasLoaded?: boolean
  isExpanded?: boolean
  children?: TreeNode[]
  minimalInfo?: boolean
  inWorkflow?: boolean
  isBlindedDocument?: boolean
  fileName?: string
  systemFolderType?: string
  templateId?: number
  virtualFolder?: boolean
  isSiteFolder?: boolean
  isStudyFolder?: boolean
  country?: { name: string }
  studyIdentifier?: string
  eosFilePicker?: {
    archived?: boolean
    levelValue?: string
    transferInProgress?: boolean
    studyId?: number
    study?: boolean
    eosStudyTransferProgressDTO?: {
      transferredSiteCount: number
      totalSiteCount: number
    }
  }
}

export interface FsiPickerNodeProps {
  // Core node data
  node: TreeNode

  // Selection state
  selectedId?: number | string | null
  selectedIds?: (number | string)[]
  partialSelectedIds?: (number | string)[]
  activeNodeId?: number | string

  // UI options
  selector?: 'all' | 'file' | 'folder'
  maxDepth?: number | null
  showRadio?: boolean
  showCheckBox?: boolean

  // Callbacks
  onSelect: (node: TreeNode) => void
  onExpand: (node: TreeNode) => void
  onCollapse: (node: TreeNode) => void
  isNodeSelectable?: (node: TreeNode) => boolean
  isNodeExpandable?: (node: TreeNode) => boolean
  removeSelectedId?: (id: number | string) => void
  addSelectedId?: (id: number | string) => void
  updatePartialSelectedIds?: (id: number | string, isRemove?: boolean) => void
  startExpandActionProgress?: () => void

  // EOS-specific props
  stateSelection?: string
  isSetupUsersAndWorkflowsFlow?: boolean
  isUserSponsorInstructionCoordinator?: boolean
  eosMediaDownloadMediFlow?: boolean
  rootId?: number
  isExpandActionInProgress?: boolean
}

const FsiPickerNode: React.FC<FsiPickerNodeProps> = (props) => {
  const context = useContext(ServiceContext)
  // fallback for extension logic if node.name is missing
  const extension = (name: string | undefined) => (name ? context?.service?.filter?.extension(name) : 'default')
  const {
    node,
    selectedId,
    selectedIds = [],
    partialSelectedIds = [],
    activeNodeId,
    selector = 'all',
    maxDepth = null,
    showRadio = false,
    showCheckBox = false,
    onSelect,
    onExpand,
    onCollapse,
    isNodeSelectable,
    isNodeExpandable,
    stateSelection,
    isSetupUsersAndWorkflowsFlow,
    isUserSponsorInstructionCoordinator,
    removeSelectedId,
    addSelectedId,
    isExpandActionInProgress,
    startExpandActionProgress,
    updatePartialSelectedIds,
    eosMediaDownloadMediFlow,
    rootId
  } = props

  // Event handlers
  const hideChildNodes = (event: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    node.isExpanded = false
    onCollapse(node)
  }

  const expandChildNodes = (event: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    startExpandActionProgress?.()
    onExpand(node)
  }

  const setCurrentNode = (event: React.MouseEvent) => {
    event.preventDefault()

    const nodeType = node.type as string
    const fileTypes = [FsiType.DOCUMENT, FsiType.PLACEHOLDER, FsiType.REFERENCE].map(String)
    const isFileSelector = selector === 'file' && fileTypes.includes(nodeType)
    const isFolderSelector = selector === 'folder' && nodeType === String(FsiType.FOLDER)
    const isAllSelector = selector === 'all'

    if (isFileSelector || isFolderSelector || isAllSelector) {
      if (isSelectable()) {
        onSelect(node)
      }
      if (isExpandable() && !node.isExpanded && !isMaxLevel()) {
        startExpandActionProgress?.()
        onExpand(node)
      }
    }
  }

  // Helper functions
  const isSelected = (checkNode: TreeNode = node): boolean => {
    if (eosMediaDownloadMediFlow && checkNode.id === rootId) {
      return false
    }
    if (showCheckBox) {
      return selectedIds.indexOf(checkNode.id) !== -1
    }

    return (
      (selectedId !== null && checkNode.id === selectedId) ||
      (selectedId !== null && checkNode.id !== -1 && checkNode.id === selectedId)
    )
  }

  const isSelectable = (): boolean => {
    return !isNodeSelectable || isNodeSelectable(node)
  }

  const isExpandable = (): boolean => {
    return !isNodeExpandable || isNodeExpandable(node)
  }

  const isMaxLevel = (): boolean => {
    return maxDepth !== null && typeof node.level === 'number' && node.level === maxDepth
  }

  const getPartialSelectionClass = (checkNode: TreeNode): string => {
    if (checkNode.id === rootId) {
      return ''
    }
    if (checkNode.children?.length) {
      const childrenArray = checkNode.children.map((child) => child.id)
      const atLeastOnePresent = childrenArray.some((id) => selectedIds.includes(id))
      const allIdsPresent = childrenArray.every((id) => selectedIds.includes(id))
      const isAnyChildPartiallySelected = checkNode.children.some((child) => partialSelectedIds.includes(child.id))
      if ((atLeastOnePresent && !allIdsPresent) || isAnyChildPartiallySelected) {
        updatePartialSelectedIds?.(checkNode.id, false)
      } else if (allIdsPresent && partialSelectedIds.indexOf(checkNode.id) !== -1) {
        updatePartialSelectedIds?.(checkNode.id, true)
      }
      return (atLeastOnePresent && !allIdsPresent) || isAnyChildPartiallySelected ? 'checked-indicator' : ''
    }
    return ''
  }

  // Constants for EOS filtering
  const ACTIVE_STATUS = 'ACTIVE'
  const STUDY_LEVEL = 'Study'

  // Recursive node renderer
  const getFsiPickerNode = (subnode: TreeNode) => {
    const fsiPickerNode = (
      <FsiPickerNode
        key={subnode.id}
        node={subnode}
        selector={selector}
        selectedId={selectedId}
        selectedIds={selectedIds}
        activeNodeId={activeNodeId}
        showRadio={showRadio}
        showCheckBox={showCheckBox}
        maxDepth={maxDepth}
        onSelect={onSelect}
        onExpand={onExpand}
        onCollapse={onCollapse}
        isNodeSelectable={isNodeSelectable}
        isNodeExpandable={isNodeExpandable}
        stateSelection={stateSelection}
        isSetupUsersAndWorkflowsFlow={isSetupUsersAndWorkflowsFlow}
        isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
        removeSelectedId={removeSelectedId}
        addSelectedId={addSelectedId}
        isExpandActionInProgress={isExpandActionInProgress}
        startExpandActionProgress={startExpandActionProgress}
        partialSelectedIds={partialSelectedIds}
        updatePartialSelectedIds={updatePartialSelectedIds}
        eosMediaDownloadMediFlow={eosMediaDownloadMediFlow}
        rootId={rootId}
      />
    )
    if (stateSelection === ACTIVE_STATUS) {
      return !subnode.eosFilePicker?.archived ? fsiPickerNode : null
    } else if (subnode.eosFilePicker?.levelValue === STUDY_LEVEL) {
      return subnode.eosFilePicker?.archived ? fsiPickerNode : null
    } else {
      return fsiPickerNode
    }
  }

  return (
    <li className={'tree-branch hide-padding' + (isSelected() ? ' selected' : '')}>
      {typeof node.level === 'number' && !node.minimalInfo && (
        <div
          className={'node-wrapper' + (isSelectable() ? ' selectable' : '')}
          onClick={setCurrentNode}
          style={{ paddingLeft: 20 * node.level }}
          title={node.name}
        >
          <div className="node">
            {/* Expand icon */}
            {!isMaxLevel() &&
              isExpandable() &&
              !node.isExpanded &&
              (node.hasChildren || !node.wasLoaded) &&
              node.type === FsiType.FOLDER && (
                <span className="plus-icon" onClick={expandChildNodes}>
                  <i />
                </span>
              )}

            {/* Collapse icon */}
            {!isMaxLevel() && isExpandable() && node.isExpanded && node.hasChildren && (
              <span className="minus-icon" onClick={hideChildNodes}>
                <i />
              </span>
            )}

            {/* Radio button */}
            {showRadio && (
              <div className={'checkbox-form-group' + (isSelectable() ? '' : ' disabled')}>
                <input
                  type="radio"
                  id={'fsiSelectorNode' + node.id}
                  name={'fsiSelectorNode' + node.id}
                  disabled={!isSelectable()}
                  checked={isSelected()}
                  onChange={() => {}}
                />
                <label htmlFor={'fsiSelectorNode' + node.id} style={{ fontWeight: 'normal' }}>
                  <div className="chkbc" />
                </label>
              </div>
            )}

            {/* Checkbox */}
            {showCheckBox && (
              <div className={'checkbox-form-group' + (isSelectable() ? '' : ' disabled')}>
                <input
                  type="checkbox"
                  className={getPartialSelectionClass(node)}
                  id={'fsiSelectorNode' + node.id}
                  name={'fsiSelectorNode' + node.id}
                  disabled={!isSelectable()}
                  checked={isSelected(node)}
                  onChange={() => {}}
                />
                <label htmlFor={'fsiSelectorNode' + node.id} style={{ fontWeight: 'normal' }}>
                  <div className="chkbc" />
                </label>
              </div>
            )}

            {/* Folder icon */}
            {node.type === FsiType.FOLDER && <span className="folder-icon" />}
            {/* Document icon (with Img) */}
            {node.type === FsiType.DOCUMENT && (
              <span className="iconca file-icon">
                <Img
                  src={getAssetPath(`assets/images/exts/${node.inWorkflow ? 'cr/' : ''}${extension(node.name)}.png`)}
                  className={'thumb-image' + (node.inWorkflow ? ' cr' : '')}
                  alt=""
                />
              </span>
            )}
            {/* Reference icon (with Img) */}
            {node.type === FsiType.REFERENCE && (
              <span className="iconca alias-icon">
                <Img
                  src={getAssetPath(`assets/images/exts/alias/${extension(node.name)}.png`)}
                  className="thumb-image cr"
                  alt=""
                />
              </span>
            )}
            {/* Placeholder icon (with Img) */}
            {node.type === FsiType.PLACEHOLDER && (
              <span className="iconca placeholder-icon">
                <Img
                  src={getAssetPath(`assets/images/exts/${node.inWorkflow ? 'cr/' : ''}placeholder.png`)}
                  className={'thumb-image' + (node.inWorkflow ? ' cr' : '')}
                  alt=""
                />
              </span>
            )}

            {/* Node name and transfer status */}
            <span
              className={`name-wrapper ${
                !isUserSponsorInstructionCoordinator &&
                isSetupUsersAndWorkflowsFlow &&
                node.eosFilePicker?.transferInProgress
                  ? 'eos-study-name-wrapper'
                  : ''
              }`}
            >
              <span className="name">{node.name}</span>
              {isSetupUsersAndWorkflowsFlow &&
                !isUserSponsorInstructionCoordinator &&
                node.eosFilePicker?.transferInProgress && (
                  <span className="message">
                    {node.eosFilePicker.eosStudyTransferProgressDTO?.transferredSiteCount &&
                    node.eosFilePicker.eosStudyTransferProgressDTO.transferredSiteCount > 0
                      ? context?.service.filter?.translate('TRANSFER_IN_PROGRESS_STATUS', {
                          sitesTransferred: node.eosFilePicker.eosStudyTransferProgressDTO.transferredSiteCount,
                          totalSites: node.eosFilePicker.eosStudyTransferProgressDTO.totalSiteCount
                        })
                      : context?.service.filter?.translate('TRANSFER_IN_PROGRESS')}
                  </span>
                )}
            </span>

            {/* Blinding status */}
            {node.isBlindedDocument === false && (
              <b className="inlist-status d-inline-block">[BLINDING_STATUS_UNBLINDED]</b>
            )}
          </div>
        </div>
      )}

      {/* Render children */}
      {node.isExpanded && node.children && (
        <ul className="subtree-container">
          {node.children.map((subnode: TreeNode) => {
            return isSetupUsersAndWorkflowsFlow ? (
              getFsiPickerNode(subnode)
            ) : (
              <FsiPickerNode
                key={`${subnode.id}_${subnode.name}`}
                node={subnode}
                selector={selector}
                selectedId={selectedId}
                selectedIds={selectedIds}
                activeNodeId={activeNodeId}
                showRadio={showRadio}
                showCheckBox={showCheckBox}
                maxDepth={maxDepth}
                onSelect={onSelect}
                onExpand={onExpand}
                onCollapse={onCollapse}
                isNodeSelectable={isNodeSelectable}
                isNodeExpandable={isNodeExpandable}
                stateSelection={stateSelection}
                isSetupUsersAndWorkflowsFlow={isSetupUsersAndWorkflowsFlow}
                isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
                removeSelectedId={removeSelectedId}
                addSelectedId={addSelectedId}
                isExpandActionInProgress={isExpandActionInProgress}
                startExpandActionProgress={startExpandActionProgress}
                partialSelectedIds={partialSelectedIds}
                updatePartialSelectedIds={updatePartialSelectedIds}
                eosMediaDownloadMediFlow={eosMediaDownloadMediFlow}
                rootId={rootId}
              />
            )
          })}
        </ul>
      )}
    </li>
  )
}

export default FsiPickerNode
